/**
 * An MF record's statuses as an admin sees them — exactly as the API returned
 * them, each labelled with the field it came from. The MF Overview and the
 * investor's MF dashboard both read them from here.
 *
 * Records carry two statuses that often disagree (live lists, 24-09-2026):
 * `webhook_status`, the latest event the provider reported, and `state`, the
 * record's own lifecycle — an order can be webhook "successful" while its
 * state is still "submitted" with no units allotted. Neither is derived from
 * the other or renamed; both are shown, each under its own label.
 *
 * Display only. Eligibility checks (can this be cancelled, redeemed from…)
 * keep their own readings of these fields.
 */

import type { MFRecord } from "../Api/mfAdminInvestments";

export interface MFStatusLine {
  label: string;
  /** The value exactly as returned; "" when the record carries the field empty. */
  value: string;
}

export const mfStatusLines = (record: MFRecord): MFStatusLine[] => {
  const lines: MFStatusLine[] = [];
  // A field the record does not carry gets no line at all (SIP modification
  // instructions have no webhook status); one it carries empty shows as "—".
  if ("webhook_status" in record) {
    lines.push({ label: "Webhook", value: String(record.webhook_status || "").trim() });
  }
  if ("state" in record) {
    lines.push({ label: "State", value: String(record.state || "").trim() });
  }
  if (!lines.length && "status" in record) {
    lines.push({ label: "Status", value: String(record.status || "").trim() });
  }
  return lines;
};

export type MFStatusTone = "success" | "pending" | "failed";

/**
 * The colour for one status value: green once it has succeeded, red once it
 * has failed, amber for anything still in progress — "confirmed" and
 * "submitted" included, since neither is an outcome yet.
 */
export const mfStatusTone = (value: string): MFStatusTone => {
  const status = value.toLowerCase();
  if (["failed", "rejected", "cancelled", "reversed", "expired"].some((word) => status.includes(word))) {
    return "failed";
  }
  if (["success", "processed", "completed", "active", "approved"].some((word) => status.includes(word))) {
    return "success";
  }
  return "pending";
};
